import { Event, getPlatformDisplayName } from '@/lib/calendar/types'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { Clock, Check } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { PdfPreview } from '@/components/ui/PdfPreview'

interface ListCalendarProps {
  selectedDate: Date
  events: Event[]
  onEventClick: (event: Event) => void
  onMoveEvent: (eventId: string, newDate: Date) => void
  onUpdateEvent: (event: Event) => void
  onViewPost?: (event: Event) => void
}

interface DraggableEventItemProps {
  event: Event
  onEventClick: (event: Event) => void
  onUpdateEvent: (event: Event) => void
  onViewPost?: (event: Event) => void
}

interface DroppableDayContainerProps {
  date: Date
  events: Event[]
  onMoveEvent: (eventId: string, newDate: Date) => void
  onEventClick: (event: Event) => void
  onUpdateEvent: (event: Event) => void
  isToday: boolean
  formatDate: (date: Date) => string
  todayRef?: React.RefObject<HTMLDivElement | null>
  onViewPost?: (event: Event) => void
}

function DraggableEventItem({
  event,
  onEventClick,
  onUpdateEvent,
  onViewPost,
}: DraggableEventItemProps) {
  const [showTimePicker, setShowTimePicker] = useState(false)
  const [tempTime, setTempTime] = useState(event.time)
  const timeButtonRef = useRef<HTMLDivElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const eventDate = new Date(event.date)
  eventDate.setHours(0, 0, 0, 0)

  const isPastEvent =
    eventDate < today || event.status === 'published' || event.source === 'tracked'

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: event.id,
    data: { event },
    disabled: isPastEvent,
  })

  // Parse time to get hours and minutes
  const getTimeComponents = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':')
    return { hours: parseInt(hours ?? '0') || 0, minutes: parseInt(minutes ?? '0') || 0 }
  }

  const { hours, minutes } = getTimeComponents(tempTime)

  const handleTimeChange = (newHours: number, newMinutes: number) => {
    const formattedTime = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`
    setTempTime(formattedTime)
  }

  const handleSaveTime = () => {
    onUpdateEvent({ ...event, time: tempTime })
    setShowTimePicker(false)
  }

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showTimePicker &&
        popupRef.current &&
        timeButtonRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        !timeButtonRef.current.contains(e.target as Node)
      ) {
        setShowTimePicker(false)
        setTempTime(event.time) // Reset to original time
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showTimePicker, event.time])

  // Render media preview based on postContent type
  const renderMediaPreview = () => {
    if (!event.postContent) {
      // Tracked/imported posts: use mediaUrl thumbnail fallback
      if (event.mediaUrl) {
        return (
          <img
            className="aspect-square h-20 w-20 shrink-0 rounded-md object-cover"
            src={event.mediaUrl}
            crossOrigin="anonymous"
            alt="Post preview"
          />
        )
      }
      return null
    }

    const content = event.postContent

    switch (content.type) {
      case 'image':
      case 'imageStory':
        return (
          <img
            className="aspect-square h-20 w-20 shrink-0 rounded-md object-cover"
            src={content.url}
            crossOrigin="anonymous"
            alt="Post preview"
          />
        )

      case 'video':
      case 'reel':
      case 'videoStory':
        return (
          <video
            className="aspect-square h-20 w-20 shrink-0 rounded-md object-cover"
            src={content.url}
          />
        )

      case 'carousel':
      case 'multiImage': {
        const urls = content.urls || []
        if (urls.length === 0) return null

        return (
          <div className="relative aspect-square h-20 w-20 shrink-0">
            {urls.slice(0, 3).map((url, index) => {
              const renderMedia = () => {
                if (typeof url === 'string') {
                  return (
                    <img
                      key={url}
                      className="h-full w-full rounded-md object-cover"
                      src={url}
                      crossOrigin="anonymous"
                    />
                  )
                } else if ('type' in url && url.type === 'video') {
                  return (
                    <video
                      key={url.url}
                      className="h-full w-full rounded-md object-cover"
                      src={url.url}
                    />
                  )
                } else {
                  // Either an IG carousel image child ({type: 'image', url}) or
                  // a LinkedIn multi-image entry ({url, altText?}) — both render
                  // as an `<img>` with `url.url` as the source.
                  return (
                    <img
                      key={url.url}
                      className="h-full w-full rounded-md object-cover"
                      src={url.url}
                      crossOrigin="anonymous"
                    />
                  )
                }
              }

              return (
                <div
                  key={typeof url === 'string' ? url : url.url}
                  className="absolute inset-0 h-full w-full"
                  style={{
                    transform: `translate(${index * 4}px, ${index * 4}px)`,
                    opacity: 1 - index * 0.3,
                    zIndex: 10 - index,
                  }}>
                  {renderMedia()}
                </div>
              )
            })}
          </div>
        )
      }

      case 'document':
        return (
          <div className="relative aspect-square h-20 w-20 shrink-0">
            <PdfPreview src={content.url} mini />
          </div>
        )

      case 'text':
      case 'reshare':
      default:
        return null
    }
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => onEventClick(event)}
      className={`bg-card border-border hover:border-primary rounded-lg border p-4 transition-all ${
        isDragging ? 'opacity-50' : ''
      } ${isPastEvent ? 'cursor-default' : 'cursor-pointer'}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {renderMediaPreview()}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h3 className="text-foreground truncate font-medium">{event.title}</h3>
            <div
              className="w-fit whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium"
              style={{
                backgroundColor: event.platformColor,
                color: 'white',
              }}>
              {getPlatformDisplayName(event.platform)}
            </div>
          </div>
        </div>
        {isPastEvent ? (
          <div className="flex items-center gap-3">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500">
              <Check className="h-4 w-4 text-white" />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                if (onViewPost) {
                  onViewPost(event)
                }
              }}>
              View Post
            </Button>
          </div>
        ) : (
          <div className="relative">
            <div
              ref={timeButtonRef}
              className="text-foreground hover:bg-hover flex cursor-pointer items-center gap-2 whitespace-nowrap rounded px-2 py-1 text-base font-medium transition-colors"
              onClick={(e) => {
                e.stopPropagation()
                setShowTimePicker(!showTimePicker)
              }}>
              <Clock className="h-5 w-5" />
              <span>{event.time}</span>
            </div>

            {showTimePicker && (
              <div
                ref={popupRef}
                className="bg-card border-border absolute right-0 top-full z-50 mt-2 min-w-[240px] rounded-lg border p-4 shadow-lg"
                onClick={(e) => e.stopPropagation()}>
                <div className="space-y-3">
                  <div className="text-foreground text-sm font-medium">Set Time</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="text-muted-foreground mb-1 block text-xs">Hours</label>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={hours}
                        onChange={(e) => handleTimeChange(parseInt(e.target.value) || 0, minutes)}
                        className="bg-background border-border text-foreground w-full rounded border px-3 py-2 text-center"
                      />
                    </div>
                    <div className="text-foreground mt-5 text-xl">:</div>
                    <div className="flex-1">
                      <label className="text-muted-foreground mb-1 block text-xs">Minutes</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={minutes}
                        onChange={(e) => handleTimeChange(hours, parseInt(e.target.value) || 0)}
                        className="bg-background border-border text-foreground w-full rounded border px-3 py-2 text-center"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowTimePicker(false)
                        setTempTime(event.time)
                      }}
                      className="flex-1">
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveTime} className="flex-1">
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function DroppableDayContainer({
  date,
  events,
  onMoveEvent: _onMoveEvent,
  onEventClick,
  onUpdateEvent,
  isToday,
  formatDate,
  todayRef,
  onViewPost,
}: DroppableDayContainerProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `list-day-${date.toISOString()}`,
    data: { date },
  })

  return (
    <div
      ref={setNodeRef}
      className={`space-y-3 rounded-lg p-3 transition-all ${
        isOver ? 'bg-primary/10 ring-primary ring-2' : ''
      }`}>
      <div className="flex items-center justify-between gap-3" ref={todayRef ?? null}>
        <h2
          className={`rounded-md px-3 py-1 text-lg font-medium ${
            isToday ? 'bg-primary text-primary-foreground' : 'text-foreground'
          }`}>
          {formatDate(date)}
        </h2>
      </div>
      <div className="space-y-2">
        {events.length > 0 ? (
          events.map((event) => (
            <DraggableEventItem
              key={event.id}
              event={event}
              onEventClick={onEventClick}
              onUpdateEvent={onUpdateEvent}
              onViewPost={onViewPost}
            />
          ))
        ) : (
          <div className="text-muted-foreground border-border rounded-lg border border-dashed py-4 text-center text-sm">
            No events scheduled
          </div>
        )}
      </div>
    </div>
  )
}

export function ListCalendar({
  selectedDate,
  events,
  onEventClick,
  onMoveEvent,
  onUpdateEvent,
  onViewPost,
}: ListCalendarProps) {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const scrollTargetRef = useRef<HTMLDivElement>(null)

  const currentMonth = selectedDate.getMonth()
  const currentYear = selectedDate.getFullYear()

  // Generate all days in the month
  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate()
  }

  const daysInMonth = getDaysInMonth(currentMonth, currentYear)
  const allDaysInMonth: Date[] = []

  for (let day = 1; day <= daysInMonth; day++) {
    allDaysInMonth.push(new Date(currentYear, currentMonth, day))
  }

  // Group events by date
  const groupedEvents: { [key: string]: Event[] } = {}

  events.forEach((event) => {
    const dateKey = event.date.toDateString()
    if (!groupedEvents[dateKey]) {
      groupedEvents[dateKey] = []
    }
    groupedEvents[dateKey].push(event)
  })

  // Sort events within each date by time
  Object.keys(groupedEvents).forEach((dateKey) => {
    groupedEvents[dateKey]?.sort((a, b) => {
      return a.time.localeCompare(b.time)
    })
  })

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const isToday = (date: Date) => {
    return date.toDateString() === today.toDateString()
  }

  const isScrollTarget = (date: Date) => {
    return date.toDateString() === selectedDate.toDateString()
  }

  const formatDate = (date: Date) => {
    const dayOfWeek = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ][date.getDay()]
    const day = date.getDate()
    return `${dayOfWeek}, ${monthNames[date.getMonth()]} ${day}`
  }

  // Scroll to the selected date (or today if no date param) on mount
  useEffect(() => {
    if (scrollTargetRef.current) {
      const container = scrollTargetRef.current.closest('.overflow-y-auto')
      if (container) {
        const elementTop = scrollTargetRef.current.offsetTop
        const offset = 24 // 24px offset from the top
        container.scrollTop = elementTop - offset
      }
    }
  }, [])

  return (
    <div className="space-y-6">
      {allDaysInMonth.map((date) => {
        const dateKey = date.toDateString()
        const dateEvents = groupedEvents[dateKey] || []
        const isTodayDate = isToday(date)
        const isTarget = isScrollTarget(date)

        return (
          <DroppableDayContainer
            key={dateKey}
            date={date}
            events={dateEvents}
            onMoveEvent={onMoveEvent}
            onEventClick={onEventClick}
            onUpdateEvent={onUpdateEvent}
            isToday={isTodayDate}
            formatDate={formatDate}
            todayRef={isTarget ? scrollTargetRef : undefined}
            onViewPost={onViewPost}
          />
        )
      })}
    </div>
  )
}
